import type { SynthesisStep, Chemical, StepStatus } from "@/types/chemistry";
import { renderFormula } from "@/lib/chemicalFormula";
import { getStepDisplayName } from "@/lib/stepUtils";
import { Input } from "@/components/ui/input";
import { ChemicalTable } from "@/components/step/ChemicalTable";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Leaf, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import { Badge } from "@/components/ui/badge";
import {
  getConnectedReactant,
  getConnectedProduct,
  getStepLabel,
  type ConnectedChemicalResult,
} from "@/lib/stepConnectivity";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface StepDetailsPanelProps {
  step: SynthesisStep;
  projectId: string;
  allSteps: SynthesisStep[];
  onUpdateStep: (updates: Partial<SynthesisStep>) => void;
  onAddChemical: (type: "reactants" | "reagents" | "products", data?: Partial<Chemical>) => void;
  onUpdateChemical: (type: "reactants" | "reagents" | "products", chemId: string, updates: Partial<Chemical>) => void;
  onRemoveChemical: (type: "reactants" | "reagents" | "products", chemId: string) => void;
  onLinkChemical: (type: "reactants" | "products", chemical: Chemical, fromStepId: string) => void;
  onViewBom: () => void;
}

const MetaRow = ({
  label, value, onChange, type, placeholder, readOnly, hint,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string; readOnly?: boolean; hint?: string }) => (
  <div className="flex items-baseline gap-2 min-w-0">
    <span className="text-sm text-muted-foreground whitespace-nowrap w-[140px] shrink-0">{label}</span>
    {readOnly ? (
      <span className={cn("h-8 flex items-center text-sm", !value && "text-muted-foreground/50 italic")}>
        {value ? (type === "date" ? format(parseISO(value), "MMM d, yyyy") : value) : (hint || "—")}
      </span>
    ) : type === "date" ? (
      <Popover>
        <PopoverTrigger asChild>
          <button className={cn("flex items-center gap-1.5 h-8 text-sm hover:text-primary transition-colors", !value && "text-muted-foreground/50")}>
            <CalendarIcon className="h-3.5 w-3.5 shrink-0" />
            {value ? format(parseISO(value), "MMM d, yyyy") : placeholder || "Pick a date"}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={value ? parseISO(value) : undefined} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} initialFocus className={cn("p-3 pointer-events-auto")} />
        </PopoverContent>
      </Popover>
    ) : (
      <Input type={type || "text"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "Enter Value"} className="h-8 text-sm border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 text-foreground placeholder:text-muted-foreground/50" />
    )}
  </div>
);

const ConnectivityBanner = ({
  result,
  type,
  currentChemicals,
  onSelect,
}: {
  result: ConnectedChemicalResult;
  type: "reactants" | "products";
  currentChemicals: Chemical[];
  onSelect: (chemical: Chemical) => void;
}) => {
  if (!result) return null;

  const stepLabel = getStepLabel(result.fromStep);
  const direction = type === "reactants" ? "←" : "→";
  const sourceLabel = type === "reactants" ? "product" : "reactant";

  if (result.type === "auto") {
    const alreadyLinked = currentChemicals.some(
      (c) => c.linkedStepId === result.fromStep.id
    );
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5 px-0.5">
        <Link2 className="h-3 w-3 shrink-0" />
        <span>
          {direction} {sourceLabel} from <span className="font-medium text-foreground">{stepLabel}</span>
          {alreadyLinked && (
            <span className="text-muted-foreground/70"> (linked)</span>
          )}
        </span>
      </div>
    );
  }

  // Multiple choices — show selector
  const linkedChem = currentChemicals.find(
    (c) => c.linkedStepId === result.fromStep.id
  );

  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5 px-0.5">
      <Link2 className="h-3 w-3 shrink-0" />
      <span className="whitespace-nowrap">
        {direction} Select {sourceLabel} from <span className="font-medium text-foreground">{stepLabel}</span>:
      </span>
      <Select
        value={linkedChem?.smiles || ""}
        onValueChange={(smiles) => {
          const chem = result.chemicals.find((c) => c.smiles === smiles);
          if (chem) onSelect(chem);
        }}
      >
        <SelectTrigger className="h-6 text-xs border-border/50 bg-transparent shadow-none px-2 w-auto min-w-[120px] gap-1">
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent>
          {result.chemicals.map((c) => (
            <SelectItem key={c.id} value={c.smiles || c.id} className="text-xs">
              {c.name || (c.formula ? renderFormula(c.formula) : null) || c.smiles || c.id.slice(0, 8)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};


export const StepDetailsPanel = ({
  step,
  projectId,
  allSteps,
  onUpdateStep,
  onAddChemical,
  onUpdateChemical,
  onRemoveChemical,
  onLinkChemical,
  onViewBom
}: StepDetailsPanelProps) => {

  const totalCO2eq = [...step.reactants, ...step.reagents, ...step.products].reduce((sum, c) => sum + (c.co2eq || 0), 0);

  const connectedReactant = getConnectedReactant(step, allSteps);
  const connectedProduct = getConnectedProduct(step, allSteps);

  return (
    <div className="h-[calc(100vh-6.5rem)] overflow-y-auto overflow-x-hidden @container">
      <div className="p-6 space-y-5">
        {/* Header */}
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Input
                value={step.name ?? ""}
                onChange={(e) => onUpdateStep({ name: e.target.value })}
                placeholder="Step name"
                className="h-8 text-lg font-bold text-primary border-0 bg-transparent px-0 shadow-none focus-visible:ring-0 placeholder:text-muted-foreground/40"
                style={{ width: `${Math.max(4, (step.name ?? "").length - 1)}ch` }}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">({step.label.replace(/^Step\s*/i, "")})</span>
            </div>
            <Badge variant="outline" className="text-[10px] h-5 gap-1 font-normal text-muted-foreground shrink-0">
              <Leaf className="h-3 w-3 text-success" />
              BoM CO₂eq: {totalCO2eq > 0 ? `${totalCO2eq.toFixed(2)} kg/mol` : "—"}
            </Badge>
          </div>
          <p className="text-[10px] text-muted-foreground/50 font-mono select-all mt-1">ID: {step.id.slice(0, 8)}</p>
        </div>

        {/* Metadata - 2 column layout, column-ordered */}
        <div className="grid grid-cols-1 @[480px]:grid-cols-2 gap-x-8 gap-y-1 @[480px]:grid-flow-col @[480px]:grid-rows-3">
          <MetaRow
            label="Earliest Delivery"
            value={step.earliestDelivery}
            onChange={() => {}}
            type="date"
            readOnly
            hint="Set reactant dates"
          />
          <MetaRow
            label="Earliest Completion"
            value={step.earliestCompletion}
            onChange={() => {}}
            readOnly
            hint="Set workdays"
          />
          <MetaRow
            label="Workdays"
            value={step.stepTime}
            onChange={(v) => onUpdateStep({ stepTime: v })}
            type="number"
            placeholder="0"
          />
          <MetaRow
            label="Yield"
            value={step.yield}
            onChange={(v) => onUpdateStep({ yield: v })}
            placeholder="Enter Value"
          />
          <MetaRow
            label="Deadline"
            value={step.deadline}
            onChange={(v) => onUpdateStep({ deadline: v })}
            type="date"
            placeholder="Pick a date"
          />
        </div>

        {/* Separator */}
        <div className="border-t" />

        {/* Chemical tables */}
        <div>
          <ConnectivityBanner
            result={connectedReactant}
            type="reactants"
            currentChemicals={step.reactants}
            onSelect={(chem) => onLinkChemical("reactants", chem, connectedReactant!.fromStep.id)}
          />
          <ChemicalTable
            title="Reactants"
            chemicals={step.reactants}
            dateLabel="Earliest Delivery"
            onAdd={() => onAddChemical("reactants")}
            onUpdate={(id, updates) => onUpdateChemical("reactants", id, updates)}
            onRemove={(id) => onRemoveChemical("reactants", id)}
            showAdd={false}
            stoichiometryMode="reactant"
            nameEditable={false}
          />
        </div>

        <ChemicalTable
          title="Reagents"
          chemicals={step.reagents}
          dateLabel="Earliest Delivery"
          onAdd={(data) => onAddChemical("reagents", data)}
          onUpdate={(id, updates) => onUpdateChemical("reagents", id, updates)}
          onRemove={(id) => onRemoveChemical("reagents", id)}
          stoichiometryMode="reagent"
          nameEditable={false}
        />

        <div>
          <ConnectivityBanner
            result={connectedProduct}
            type="products"
            currentChemicals={step.products}
            onSelect={(chem) => onLinkChemical("products", chem, connectedProduct!.fromStep.id)}
          />
          <ChemicalTable
            title="Products"
            chemicals={step.products}
            dateLabel="Earliest Compl."
            onAdd={() => onAddChemical("products")}
            onUpdate={(id, updates) => onUpdateChemical("products", id, updates)}
            onRemove={(id) => onRemoveChemical("products", id)}
            showAdd={false}
            showCo2={false}
            nameEditable={false}
          />
        </div>
      </div>
    </div>
  );
};
